import { useState, useEffect } from "react"
import { getDatabase, ref, onValue } from "firebase/database"
import { FaEye, FaDownload, FaSearch } from "react-icons/fa"
import "./finance.css"
import InfoIcon from "./infoIcon"

const SupplierFinance = () => {
  const [financeData, setFinanceData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [viewingLoan, setViewingLoan] = useState(null)
  const [sortConfig, setSortConfig] = useState({ key: "timestamp", direction: "desc" })

  useEffect(() => {
    const fetchFinanceData = async () => {
      try {
        const db = getDatabase()
        const financeRef = ref(db, "Finance")

        onValue(
          financeRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val()
              const financeList = []

              // First level: user IDs
              Object.keys(data).forEach((userId) => {
                // Second level: application IDs
                if (typeof data[userId] === "object") {
                  Object.keys(data[userId]).forEach((applicationId) => {
                    const applicationData = data[userId][applicationId]

                    // Only add if it's a valid application object with Flutter structure
                    if (applicationData && typeof applicationData === "object" && applicationData.personalInfo) {
                      // Validate timestamp
                      const timestamp = applicationData.timestamp
                      if (timestamp && !isNaN(new Date(timestamp).getTime())) {
                        financeList.push({
                          userId,
                          applicationId,
                          timestamp,
                          ...applicationData,
                        })
                      } else {
                        console.warn(`Invalid timestamp for application ${applicationId}: ${timestamp}`)
                      }
                    }
                  })
                }
              })

              // Sort by timestamp (newest first)
              financeList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              console.log("Sorted finance data:", financeList)
              setFinanceData(financeList)
              setIsLoading(false)
            } else {
              setFinanceData([])
              setError("No finance data available.")
              setIsLoading(false)
            }
          },
          (error) => {
            console.error("Error fetching finance data:", error)
            setError("Error fetching finance data: " + error.message)
            setIsLoading(false)
          },
        )
      } catch (err) {
        console.error("Error in finance data fetch:", err)
        setError("Error fetching finance data: " + err.message)
        setIsLoading(false)
      }
    }

    fetchFinanceData()
  }, [])

  const handleSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current && current[key], obj) || ""
  }

  const sortedData = [...financeData].sort((a, b) => {
    let aValue = getNestedValue(a, sortConfig.key)
    let bValue = getNestedValue(b, sortConfig.key)

    // Special handling for timestamp to ensure newest first
    if (sortConfig.key === "timestamp") {
      aValue = new Date(aValue).getTime()
      bValue = new Date(bValue).getTime()
      if (isNaN(aValue) || isNaN(bValue)) {
        console.warn(`Invalid timestamp in sorting: a=${aValue}, b=${bValue}`)
        return 0
      }
    } else {
      if (typeof aValue === "string") aValue = aValue.toLowerCase()
      if (typeof bValue === "string") bValue = bValue.toLowerCase()
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1
    }
    return 0
  })

  const filteredData = sortedData.filter((item) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      (item.personalInfo?.firstName && item.personalInfo.firstName.toLowerCase().includes(searchLower)) ||
      (item.personalInfo?.lastName && item.personalInfo.lastName.toLowerCase().includes(searchLower)) ||
      (item.personalInfo?.email && item.personalInfo.email.toLowerCase().includes(searchLower)) ||
      (item.personalInfo?.company && item.personalInfo.company.toLowerCase().includes(searchLower)) ||
      (item.financialDetails?.loanType && item.financialDetails.loanType.toLowerCase().includes(searchLower)) ||
      (item.financialDetails?.loanAmount && item.financialDetails.loanAmount.toString().includes(searchTerm)) ||
      (item.applicationId && item.applicationId.toLowerCase().includes(searchLower))
    )
  })

  const handleViewLoan = (loan) => {
    console.log("Opening modal for loan:", loan)
    setViewingLoan(loan)
  }

  const closeModal = () => {
    console.log("Closing modal")
    setViewingLoan(null)
  }

  const exportToCSV = () => {
    const headers = [
      "Application ID",
      "First Name",
      "Last Name",
      "Company Name",
      "Email",
      "Phone",
      "PAN",
      "GSTIN",
      "Date of Birth",
      "Address",
      "Loan Type",
      "Loan Amount",
      "Tenure",
      "Monthly Income",
      "Employment Type",
      "Company",
      "Documents Status",
      "Status",
      "Timestamp",
    ]

    const csvData = [
      headers.join(","),
      ...filteredData.map((item) =>
        [
          `"${item.applicationId || ""}"`,
          `"${item.personalInfo?.firstName || ""}"`,
          `"${item.personalInfo?.lastName || ""}"`,
          `"${item.personalInfo?.company || ""}"`,
          `"${item.personalInfo?.email || ""}"`,
          `"${item.personalInfo?.phone || ""}"`,
          `"${item.personalInfo?.pan || ""}"`,
          `"${item.personalInfo?.gstin || ""}"`,
          `"${item.personalInfo?.dateOfBirth || ""}"`,
          `"${item.personalInfo?.address || ""}"`,
          `"${item.financialDetails?.loanType || ""}"`,
          `"${item.financialDetails?.loanAmount || ""}"`,
          `"${item.financialDetails?.tenure || ""}"`,
          `"${item.financialDetails?.monthlyIncome || ""}"`,
          `"${item.financialDetails?.employmentType || ""}"`,
          `"${item.financialDetails?.company || ""}"`,
          `"${
            item.documents
              ? Object.entries(item.documents)
                  .map(([key, value]) => `${key}:${value}`)
                  .join(";")
              : ""
          }"`,
          `"${item.status || ""}"`,
          `"${item.timestamp || ""}"`,
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", `finance_data_${new Date().toISOString().split("T")[0]}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getDocumentStatusIcon = (documents) => {
    if (!documents) return "❌"
    const uploadedCount = Object.values(documents).filter((status) => status === "yes").length
    const totalCount = Object.keys(documents).length
    if (uploadedCount === 0) return "❌"
    if (uploadedCount === totalCount) return "✅"
    return "⚠️"
  }

  if (isLoading) {
    return (
      <div className="finance-loading">
        <div className="spinner"></div>
        <p>Loading finance data...</p>
      </div>
    )
  }

  if (error) {
    return <div className="finance-error">{error}</div>
  }

  return (
    <div className="finance-container">
      <div className="finance-header">
        <h1>Finance Management <InfoIcon text="This platform facilitates loan applications in partnership with Utkarsh Small Finance Bank Limited, licensed and regulated by the Reserve Bank of India (RBI). We act only as a Direct Selling Agent (DSA) and technology platform, assisting users in applying for loans. Loan approval and disbursal are solely managed by the bank.

Lender Details:

Company: Utkarsh Small Finance Bank Limited

CIN: L65992UP2016PLC082804

RBI License: Licence No. MUM: 125 (issued Nov 25, 2016, under Section 22(1) of the Banking Regulation Act, 1949)" /></h1>
        <div className="finance-actions">
          <div className="search-container">
               <FaSearch className="user-search-icon" /> 
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              maxLength={100}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="export-btn" onClick={exportToCSV}>
            <FaDownload /> Export to CSV
          </button>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="no-finance-data">
          <p>No finance data found matching your search criteria.</p>
        </div>
      ) : (
        <div className="finance-table-container">
          <table className="finance-table">
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => handleSort("applicationId")}>Application ID</th>
                <th onClick={() => handleSort("personalInfo.firstName")}>Name</th>
                <th onClick={() => handleSort("personalInfo.company")}>Company</th>
                <th onClick={() => handleSort("financialDetails.loanType")}>Loan Type</th>
                <th onClick={() => handleSort("financialDetails.loanAmount")}>Amount</th>
                <th onClick={() => handleSort("personalInfo.phone")}>Phone</th>
                <th>Documents</th>
                <th onClick={() => handleSort("status")}>Status</th>
                <th onClick={() => handleSort("timestamp")}>Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((loan, index) => (
                <tr key={loan.applicationId || index} className={index % 2 === 0 ? "even-row" : "odd-row"}>
                  <td>{index + 1}</td>
                  <td>{loan.applicationId || "N/A"}</td>
                  <td>
                    {`${loan.personalInfo?.firstName || ""} ${loan.personalInfo?.lastName || ""}`.trim() || "N/A"}
                  </td>
                  <td>{loan.personalInfo?.company || "N/A"}</td>
                  <td>{loan.financialDetails?.loanType || "N/A"}</td>
                  <td>₹{loan.financialDetails?.loanAmount || "0"}</td>
                  <td>{loan.personalInfo?.phone || "N/A"}</td>
                  <td className="Document-Status">{getDocumentStatusIcon(loan.documents)}</td>
                  <td>
                    <span className={`status-badge ${loan.status || "unknown"}`}>{loan.status || "Unknown"}</span>
                  </td>
                  <td>{new Date(loan.timestamp).toLocaleString() || "N/A"}</td>
                  <td>
                    <button className="view-btn" onClick={() => handleViewLoan(loan)}>
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingLoan && (
        <div className="finance-container supplier-modal-overlay" onClick={closeModal}>
          <div className="finance-container supplier-loan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="finance-container supplier-modal-header">
              <h3>Loan Application Details</h3>
              <button className="finance-container supplier-close-modal-btn" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="finance-container supplier-modal-content">
              <div className="finance-container supplier-loan-detail-grid">
                <div className="detail-group">
                  <h4>Application Info</h4>
                  <div className="detail-item">
                    <span className="detail-label">Application ID:</span>
                    <span className="detail-value">{viewingLoan.applicationId || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-badge ${viewingLoan.status || "unknown"}`}>
                      {viewingLoan.status || "Unknown"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Submitted On:</span>
                    <span className="detail-value">
                      {viewingLoan.timestamp ? new Date(viewingLoan.timestamp).toLocaleString() : "N/A"}
                    </span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Personal Information</h4>
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">
                      {`${viewingLoan.personalInfo?.firstName || ""} ${viewingLoan.personalInfo?.lastName || ""}`.trim() ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.email || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.phone || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">PAN:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.pan || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">GSTIN:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.gstin || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date of Birth:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.dateOfBirth || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Address:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.address || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Company:</span>
                    <span className="detail-value">{viewingLoan.personalInfo?.company || "N/A"}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Financial Details</h4>
                  <div className="detail-item">
                    <span className="detail-label">Loan Type:</span>
                    <span className="detail-value">{viewingLoan.financialDetails?.loanType || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Loan Amount:</span>
                    <span className="detail-value">₹{viewingLoan.financialDetails?.loanAmount || "0"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tenure:</span>
                    <span className="detail-value">{viewingLoan.financialDetails?.tenure || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Monthly Income:</span>
                    <span className="detail-value">₹{viewingLoan.financialDetails?.monthlyIncome || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Employment Type:</span>
                    <span className="detail-value">{viewingLoan.financialDetails?.employmentType || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Company/Organization:</span>
                    <span className="detail-value">{viewingLoan.financialDetails?.company || "N/A"}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h4>Document Status</h4>
                  {viewingLoan.documents ? (
                    Object.entries(viewingLoan.documents).map(([docType, status]) => (
                      <div key={docType} className="detail-item">
                        <span className="detail-label">{docType.charAt(0).toUpperCase() + docType.slice(1)}:</span>
                        <span className={`detail-value Document-Status-${status}`}>
                          {status === "yes" ? "✅ Uploaded" : "❌ Not Uploaded"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="detail-item">
                      <span className="detail-value">No document information available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupplierFinance